/** Признак пачки бэкенд пока не отдаёт, поэтому есть второй режим — по зоне, типу и дню */

import { MmCompetitorStore, MmMonitoringResult, MmTask } from '../../types/domain';
import { MmTaskStatus, MmTaskType } from '../../types/enums';
import { MmResultListFilters } from '../../types/dto';

/** Поле заполняется, только если совпадает у всех заданий пачки; разошлось — null и прочерк */
export interface MmMonitoringBatch {
  key: string;
  /** ID пачки. `null` ⇒ пачка собрана приближённо, точного признака нет. */
  batchId: string | null;
  /** true — пачка собрана по типу, зоне и дню, а не по batchId */
  isApproximate: boolean;
  /** null — у заданий пачки разошлись типы */
  type: MmTaskType | null;
  createdAt: string | null;

  storeFormatId: number | null;
  storeFormatName: string | null;
  storeRegionId: number | null;
  storeRegionName: string | null;
  storeClusterId: number | null;
  storeClusterName: string | null;

  taskIds: string[];
  /** Задания отсортированы по коду магазина конкурента */
  tasks: MmTask[];
  competitorCodes: string[];

  tasksTotal: number;
  /** Считаются завершённые, принятые и отклонённые задания */
  tasksDone: number;
  tasksInProgress: number;
  /** Пусто означает и «доехало всё», и «признака нет» — полноту фронт не вычисляет */
  incompleteTaskIds: string[];
}

/** Префиксы не дают ключу совпасть с членом Object */
const EXACT_KEY_PREFIX = 'batch:';
const APPROXIMATE_KEY_PREFIX = 'approx:';
/** Разделитель частей приближённого ключа — в кодах и именах не встречается. */
const KEY_SEPARATOR = '|';

function isFilledString(value: string | null | undefined): boolean {
  return value !== null && value !== undefined && value !== '';
}

function hasOwn(dictionary: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(dictionary, key);
}

function keyPart(value: number | null | undefined): string {
  return value === null || value === undefined ? '-' : String(value);
}

function extractCalendarDay(createdAt: string): string {
  const raw = createdAt.trim();
  const separators = ['T', ' ', ','];
  let cut = raw.length;

  for (let i = 0; i < separators.length; i += 1) {
    const index = raw.indexOf(separators[i]);

    if (index > 0 && index < cut) {
      cut = index;
    }
  }

  return raw.substring(0, cut);
}

/** Задание без createdAt — пачка из одного; в приблизительный ключ входит вся зона, не только тип */
function batchKeyOfTask(task: MmTask): string {
  if (isFilledString(task.batchId)) {
    return EXACT_KEY_PREFIX + String(task.batchId);
  }

  if (!isFilledString(task.createdAt)) {
    return APPROXIMATE_KEY_PREFIX + 'nodate' + KEY_SEPARATOR + String(task.id);
  }

  return (
    APPROXIMATE_KEY_PREFIX +
    String(task.type) +
    KEY_SEPARATOR +
    keyPart(task.storeFormatId) +
    KEY_SEPARATOR +
    keyPart(task.storeRegionId) +
    KEY_SEPARATOR +
    keyPart(task.storeClusterId) +
    KEY_SEPARATOR +
    extractCalendarDay(String(task.createdAt))
  );
}

function toTime(value: string | null): number | null {
  if (!isFilledString(value)) {
    return null;
  }

  const time = Date.parse(String(value));

  return isNaN(time) ? null : time;
}

function isEarlier(left: string, right: string): boolean {
  const leftTime = toTime(left);
  const rightTime = toTime(right);

  if (leftTime !== null && rightTime !== null) {
    return leftTime < rightTime;
  }

  return left < right;
}

function sharedValue<T>(tasks: MmTask[], pick: (task: MmTask) => T): T | null {
  if (tasks.length === 0) {
    return null;
  }

  const first = pick(tasks[0]);

  for (let i = 1; i < tasks.length; i += 1) {
    if (pick(tasks[i]) !== first) {
      return null;
    }
  }

  return first;
}

/** Судим только по явным флагам: у сплошного результатов законно меньше, чем позиций. */
function isTaskDeliveryIncomplete(task: MmTask): boolean {
  if (task.isPartiallyDelivered === true) {
    return true;
  }

  return task.isDeliveryComplete === false;
}

/** positionsTotal в ответе — число результатов подзадачи, а не длина плана */
export function isTaskAwaitingClose(task: MmTask): boolean {
  if (task.status !== MmTaskStatus.ASSIGNED) {
    return false;
  }

  return typeof task.positionsTotal === 'number' && task.positionsTotal > 0;
}

export function resolvePositionsTotal(
  task: MmTask,
  loadedResultCount: number | null | undefined,
): number | null {
  const fromRows = typeof loadedResultCount === 'number' && !isNaN(loadedResultCount)
    ? loadedResultCount
    : 0;
  const fromServer = task.positionsTotal;

  if (typeof fromServer === 'number' && !isNaN(fromServer)) {
    return fromServer > fromRows ? fromServer : fromRows;
  }

  return fromRows > 0 ? fromRows : fromServer;
}

export function taskHasLoadedResults(
  taskId: string,
  resultCountByTaskId: { [taskId: string]: number },
): boolean {
  const count = resultCountByTaskId[taskId];

  return typeof count === 'number' && count > 0;
}

function compareTasksByCompetitor(left: MmTask, right: MmTask): number {
  const leftCode = isFilledString(left.competitorCode) ? String(left.competitorCode) : '';
  const rightCode = isFilledString(right.competitorCode) ? String(right.competitorCode) : '';

  if (leftCode !== rightCode) {
    return leftCode < rightCode ? -1 : 1;
  }

  // Одинаковые коды в пачке — аномалия, но порядок обязан быть устойчивым между перерисовками
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}

function buildBatch(key: string, bucket: MmTask[]): MmMonitoringBatch {
  const tasks = bucket.slice().sort(compareTasksByCompetitor);
  const batchId = isFilledString(tasks[0].batchId) ? String(tasks[0].batchId) : null;

  const taskIds: string[] = [];
  const competitorCodes: string[] = [];
  const incompleteTaskIds: string[] = [];
  const seenCodes: { [code: string]: boolean } = {};

  let createdAt: string | null = null;
  let tasksDone = 0;
  let tasksInProgress = 0;

  for (let i = 0; i < tasks.length; i += 1) {
    const task = tasks[i];

    taskIds.push(task.id);

    if (isFilledString(task.competitorCode)) {
      const code = String(task.competitorCode);

      if (!hasOwn(seenCodes, code)) {
        seenCodes[code] = true;
        competitorCodes.push(code);
      }
    }

    if (isTaskDeliveryIncomplete(task)) {
      incompleteTaskIds.push(task.id);
    }

    if (
      task.status === MmTaskStatus.COMPLETED ||
      task.status === MmTaskStatus.ACCEPTED ||
      task.status === MmTaskStatus.REJECTED
    ) {
      tasksDone += 1;
    } else if (task.status === MmTaskStatus.ASSIGNED) {
      tasksInProgress += 1;
    }

    if (isFilledString(task.createdAt)) {
      const moment = String(task.createdAt);

      if (createdAt === null || isEarlier(moment, createdAt)) {
        createdAt = moment;
      }
    }
  }

  return {
    key: key,
    batchId: batchId,
    isApproximate: batchId === null,
    type: sharedValue(tasks, function (task: MmTask): MmTaskType {
      return task.type;
    }),
    createdAt: createdAt,
    storeFormatId: sharedValue(tasks, function (task: MmTask): number | null {
      return task.storeFormatId;
    }),
    storeFormatName: sharedValue(tasks, function (task: MmTask): string | null {
      return task.storeFormatName;
    }),
    storeRegionId: sharedValue(tasks, function (task: MmTask): number | null {
      return task.storeRegionId;
    }),
    storeRegionName: sharedValue(tasks, function (task: MmTask): string | null {
      return task.storeRegionName;
    }),
    storeClusterId: sharedValue(tasks, function (task: MmTask): number | null {
      return task.storeClusterId;
    }),
    storeClusterName: sharedValue(tasks, function (task: MmTask): string | null {
      return task.storeClusterName;
    }),
    taskIds: taskIds,
    tasks: tasks,
    competitorCodes: competitorCodes,
    tasksTotal: tasks.length,
    tasksDone: tasksDone,
    tasksInProgress: tasksInProgress,
    incompleteTaskIds: incompleteTaskIds,
  };
}

export function groupTasksIntoBatches(tasks: MmTask[]): MmMonitoringBatch[] {
  const source = tasks !== null && tasks !== undefined ? tasks : [];
  const buckets: { [key: string]: MmTask[] } = {};
  const order: string[] = [];

  for (let i = 0; i < source.length; i += 1) {
    const task = source[i];

    if (task === null || task === undefined) {
      continue;
    }

    const key = batchKeyOfTask(task);

    if (!hasOwn(buckets, key)) {
      buckets[key] = [];
      order.push(key);
    }

    buckets[key].push(task);
  }

  const batches: MmMonitoringBatch[] = [];

  for (let i = 0; i < order.length; i += 1) {
    batches.push(buildBatch(order[i], buckets[order[i]]));
  }

  batches.sort(function (left: MmMonitoringBatch, right: MmMonitoringBatch): number {
    const hasLeft = left.createdAt !== null;
    const hasRight = right.createdAt !== null;

    if (hasLeft !== hasRight) {
      return hasLeft ? -1 : 1;
    }

    if (hasLeft && hasRight && left.createdAt !== right.createdAt) {
      return isEarlier(String(left.createdAt), String(right.createdAt)) ? 1 : -1;
    }

    return left.key < right.key ? -1 : left.key > right.key ? 1 : 0;
  });

  return batches;
}

export function findBatchByKey(
  batches: MmMonitoringBatch[],
  key: string | null,
): MmMonitoringBatch | null {
  if (!isFilledString(key) || batches === null || batches === undefined) {
    return null;
  }

  for (let i = 0; i < batches.length; i += 1) {
    if (batches[i].key === key) {
      return batches[i];
    }
  }

  return null;
}

export function findBatchOfTask(
  batches: MmMonitoringBatch[],
  taskId: string | null,
): MmMonitoringBatch | null {
  if (!isFilledString(taskId) || batches === null || batches === undefined) {
    return null;
  }

  for (let i = 0; i < batches.length; i += 1) {
    if (batches[i].taskIds.indexOf(String(taskId)) !== -1) {
      return batches[i];
    }
  }

  return null;
}

export function resolveBatchKeyForNavigation(
  batches: MmMonitoringBatch[],
  batchId: string | null,
  taskId: string | null,
): string | null {
  if (isFilledString(batchId)) {
    const exactKey = EXACT_KEY_PREFIX + String(batchId);
    const exact = findBatchByKey(batches, exactKey);

    if (exact !== null) {
      return exactKey;
    }
  }

  const byTask = findBatchOfTask(batches, taskId);

  return byTask !== null ? byTask.key : null;
}

/** Пустой массив taskIds значит «ничего не выбрано», а не «все задания» */
export function buildBatchScopeFilters(
  batch: MmMonitoringBatch | null,
): Partial<MmResultListFilters> {
  if (batch === null || batch === undefined || batch.taskIds.length === 0) {
    return { taskIds: [] };
  }

  // Копия: снаружи могут изменить массив фильтра и молча переписать состав пачки
  return { taskIds: batch.taskIds.slice() };
}

/** Признаков доставки нет — считаем пачку полной: блокировать приёмку по догадке нельзя */
export function isBatchFullyDelivered(batch: MmMonitoringBatch | null): boolean {
  if (batch === null || batch === undefined) {
    return true;
  }

  return batch.incompleteTaskIds.length === 0;
}

function readStoreLabel(store: MmCompetitorStore | null | undefined): string | null {
  if (store === null || store === undefined) {
    return null;
  }

  const name = store.name;

  if (name !== null && name !== undefined && String(name).trim() !== '') {
    return String(name);
  }

  const network = store.competitorNetworkName;

  if (network !== null && network !== undefined && String(network).trim() !== '') {
    return String(network);
  }

  return null;
}

export function decorateResultsWithTaskInfo(
  rows: MmMonitoringResult[],
  batch: MmMonitoringBatch | null,
): MmMonitoringResult[] {
  const source = rows !== null && rows !== undefined ? rows : [];

  if (batch === null || batch === undefined || batch.tasks.length === 0) {
    return source.slice();
  }

  const byTaskId: { [taskId: string]: MmTask } = {};

  for (let i = 0; i < batch.tasks.length; i += 1) {
    byTaskId[batch.tasks[i].id] = batch.tasks[i];
  }

  const decorated: MmMonitoringResult[] = [];

  for (let i = 0; i < source.length; i += 1) {
    const row = source[i];

    if (row === null || row === undefined) {
      continue;
    }

    if (!isFilledString(row.taskId) || !hasOwn(byTaskId, row.taskId)) {
      decorated.push(row);
      continue;
    }

    const task = byTaskId[row.taskId];
    const store = task.competitorStore;
    const storeName = readStoreLabel(store);
    const existingLogin = row.auditorLogin;

    decorated.push(
      Object.assign({}, row, {
        taskType: task.type,
        taskDisplayNumber: task.displayNumber,
        taskCreatedAt: task.createdAt,
        competitorCode: task.competitorCode,
        competitorStoreName: storeName,
        auditorLogin: existingLogin !== null && existingLogin !== undefined && existingLogin !== ''
          ? existingLogin
          : task.auditorLogin,
        auditorFullName: task.auditorFullName,
      }),
    );
  }

  return decorated;
}
